import type { SupplierView } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input } from 'antd';
import { useEffect } from 'react';
import { DirectoryTagsField } from '../../../components/DirectoryTagsField';
import type { DirectoryTagView } from '../../directory-tags/directory-tags.api';
import { SupplierLocationSearch } from './SupplierLocationSearch';

interface SupplierFormDrawerProps {
  open: boolean;
  /** Present to edit that supplier; absent to create a new one. */
  editing?: SupplierView;
  catalogue: DirectoryTagView[];
  error: string | null;
  submitting: boolean;
  onSubmit: (values: Record<string, unknown>) => Promise<boolean>;
  onClose: () => void;
}

function toFormValues(editing: SupplierView | undefined): Record<string, unknown> {
  if (!editing) return { name: '', location: '', phone: '', email: '', categories: [], specialities: '' };
  return {
    name: editing.name,
    location: editing.location,
    phone: editing.phone,
    email: editing.email,
    categories: editing.categories,
    specialities: editing.specialities,
  };
}

/** Create or edit a supplier: no truck is driven there, so this stays the short form — just a place search to fill in the location better. */
export function SupplierFormDrawer({ open, editing, catalogue, error, submitting, onSubmit, onClose }: SupplierFormDrawerProps) {
  const [form] = Form.useForm<Record<string, unknown>>();

  useEffect(() => {
    if (open) form.setFieldsValue(toFormValues(editing));
  }, [open, editing, form]);

  async function submit(values: Record<string, unknown>) {
    if (await onSubmit(values)) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? `Edit ${editing.name}` : 'New supplier'} placement="right" size={420} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input autoFocus />
        </Form.Item>
        <Form.Item label="Search for the location" extra="Picking a result fills in the location below." className="mb-1">
          <SupplierLocationSearch />
        </Form.Item>
        <Form.Item name="location" label="Location" rules={[{ required: true, message: 'Enter a location' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Phone">
          <Input inputMode="tel" placeholder="0712 345 678" />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Enter a valid email address' }]}>
          <Input />
        </Form.Item>
        <DirectoryTagsField
          name="categories"
          label="Categories"
          extra="What this supplier stocks — filters, brakes, tyres. Type a value and press enter."
          catalogue={catalogue}
          carried={editing?.categories ?? []}
        />
        <Form.Item name="specialities" label="Specialities">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create supplier'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
